import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from '../librarianSwal';
import { useAuth } from '../../Constant/Auth';
import { useActiveLibraryId, useSectionBase } from '../../Constant/useActiveLibrary';
import SearchableSelect from '../../shared/SearchableSelect';

import {
  getIssueById,
  returnBookIssue,
  createLateFineFromIssue,
  getFineRulesByLibrary,
  getCopyByScanCode,
  getMemberByScanCode,
  getErrorMessage
} from '../../api/api';

import './ReturnBook.css';
import '../lib-page-header.css';

const todayStr = () => new Date().toISOString().slice(0, 10);

const ReturnBook = () => {
  const navigate = useNavigate();
  const sectionBase = useSectionBase();
  const { id } = useParams();
  const { user } = useAuth();

  const libraryId = useActiveLibraryId() || user?.libraryId;
  const staffId = user?.id || user?.staff_id;

  const [issue, setIssue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [returnDate, setReturnDate] = useState(todayStr());
  const [condition, setCondition] = useState('good');
  const [remarks, setRemarks] = useState('');

  // Late-fine section (only relevant when overdue)
  const [applyFine, setApplyFine] = useState(false);
  const [fineAmount, setFineAmount] = useState('');
  const [fineReason, setFineReason] = useState('');
  const [perDayRate, setPerDayRate] = useState(0);

  // Scan-verify section
  const [bookScan, setBookScan] = useState('');
  const [memberScan, setMemberScan] = useState('');
  const [bookVerify, setBookVerify] = useState(null);   // null | 'ok' | 'mismatch'
  const [memberVerify, setMemberVerify] = useState(null);
  const [verifyLoading, setVerifyLoading] = useState({ book: false, member: false });

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await getIssueById(id);
        setIssue(data || null);

        // Try to pick up a per-day late-fine rate from the library's fine rules
        if (libraryId) {
          try {
            const rules = await getFineRulesByLibrary(libraryId);
            const lateRule = (rules || []).find(
              (r) => String(r.fine_type || r.rule_type || '').toLowerCase() === 'late'
            );
            if (lateRule) {
              setPerDayRate(
                Number(
                  lateRule.late_fine_per_day ??
                    lateRule.per_day_amount ??
                    lateRule.amount ??
                    0
                )
              );
            }
          } catch {
            /* fine rules are optional — ignore */
          }
        }
      } catch (error) {
        Swal.fire('Error', getErrorMessage(error), 'error');
      } finally {
        setLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, libraryId]);

  const status = String(issue?.issue_status || issue?.status || 'issued').toLowerCase();
  const alreadyReturned = status === 'returned';

  const bookName = issue?.title || issue?.book_title || issue?.book_name || '-';
  const memberName =
    `${issue?.first_name || ''} ${issue?.last_name || ''}`.trim() ||
    issue?.member_name ||
    issue?.full_name ||
    issue?.student_name ||
    '-';
  const copyText = issue?.accession_no || issue?.barcode || issue?.book_copy_id || '-';

  // Overdue days = returnDate - dueDate (clamped at 0)
  const overdueDays = useMemo(() => {
    if (!issue?.due_date || !returnDate) return 0;
    const due = new Date(String(issue.due_date).slice(0, 10));
    const ret = new Date(returnDate);
    const diff = Math.floor((ret - due) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  }, [issue, returnDate]);

  const suggestedFine = useMemo(
    () => overdueDays * Number(perDayRate || 0),
    [overdueDays, perDayRate]
  );

  // When the book becomes overdue, default the fine section on and pre-fill it
  useEffect(() => {
    if (overdueDays > 0) {
      setApplyFine(true);
      setFineReason((prev) => prev || `Late return — ${overdueDays} day(s) overdue`);
      setFineAmount((prev) => (prev === '' && suggestedFine > 0 ? String(suggestedFine) : prev));
    } else {
      setApplyFine(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overdueDays, suggestedFine]);

  const handleVerifyBook = async (code) => {
    if (!code.trim() || !libraryId) return;
    setVerifyLoading((prev) => ({ ...prev, book: true }));
    try {
      const copy = await getCopyByScanCode(libraryId, code.trim());
      const issueCopyId = String(issue?.book_copy_id || '');
      const match =
        String(copy?.id || '') === issueCopyId ||
        (copy?.accession_no && copy.accession_no === issue?.accession_no) ||
        (copy?.barcode && copy.barcode === issue?.barcode);
      setBookVerify(match ? 'ok' : 'mismatch');
    } catch {
      setBookVerify('mismatch');
    } finally {
      setVerifyLoading((prev) => ({ ...prev, book: false }));
      setBookScan('');
    }
  };

  const handleVerifyMember = async (code) => {
    if (!code.trim() || !libraryId) return;
    setVerifyLoading((prev) => ({ ...prev, member: true }));
    try {
      const member = await getMemberByScanCode(libraryId, code.trim());
      const issueMemberId = String(issue?.member_id || issue?.user_id || issue?.parent_id || '');
      const match = String(member?.id || '') === issueMemberId;
      setMemberVerify(match ? 'ok' : 'mismatch');
    } catch {
      setMemberVerify('mismatch');
    } finally {
      setVerifyLoading((prev) => ({ ...prev, member: false }));
      setMemberScan('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!returnDate) {
      Swal.fire('Required', 'Please select a return date', 'warning');
      return;
    }
    if (issue?.issue_date && new Date(returnDate) < new Date(String(issue.issue_date).slice(0, 10))) {
      Swal.fire('Invalid date', 'Return date cannot be before the issue date', 'warning');
      return;
    }
    if (applyFine && (fineAmount === '' || Number(fineAmount) < 0)) {
      Swal.fire('Invalid fine', 'Please enter a valid fine amount', 'warning');
      return;
    }

    const confirm = await Swal.fire({
      title: 'Confirm return?',
      html: `Return <b>${bookName}</b> for <b>${memberName}</b>${
        applyFine && Number(fineAmount) > 0
          ? `<br/>A late fine of <b>₹ ${Number(fineAmount).toLocaleString('en-IN')}</b> will be created.`
          : ''
      }`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, return'
    });
    if (!confirm.isConfirmed) return;

    try {
      setSaving(true);

      await returnBookIssue(id, {
        return_date: returnDate,
        book_return_condition: condition,
        remarks: remarks || null,
        returned_to_staff_id: staffId
      });

      // Create the late fine only when requested and amount > 0
      if (applyFine && Number(fineAmount) > 0) {
        try {
          await createLateFineFromIssue(id, {
            library_id: libraryId,
            member_id: issue?.member_id || issue?.user_id || issue?.parent_id,
            amount: Number(fineAmount),
            paid_amount: 0,
            balance_amount: Number(fineAmount),
            fine_type: 'late',
            fine_status: 'pending',
            reason: fineReason || 'Late return fine',
            created_by_staff_id: staffId
          });
        } catch (fineError) {
          // Book is already returned; surface the fine failure but don't block
          Swal.fire(
            'Returned, but fine failed',
            `Book returned successfully, but the late fine could not be created: ${getErrorMessage(
              fineError
            )}`,
            'warning'
          );
          navigate(`${sectionBase}/issues`);
          return;
        }
      }

      await Swal.fire('Returned', 'Book returned successfully', 'success');
      navigate(`${sectionBase}/issues`);
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="rb-page">
        <div className="rb-card">
          <div className="rb-state">
            <i className="ti ti-loader-2 spin" /> Loading issue...
          </div>
        </div>
      </div>
    );
  }

  if (!issue) {
    return (
      <div className="rb-page">
        <div className="rb-card">
          <div className="rb-state">
            <h4 style={{ color: 'var(--rb-ink)', marginBottom: 8 }}>Issue not found</h4>
            <p>This issue record could not be loaded.</p>
            <button className="rb-btn rb-btn-primary" onClick={() => navigate(`${sectionBase}/issues`)}>
              Back to Issued Books
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rb-page">
      <div className="lib-page-header">
        <div className="lib-page-header__left">
          <div className="lib-page-header__icon">
            <i className="fas fa-undo-alt" />
          </div>
          <div>
            <h3 className="lib-page-header__title">Return Book</h3>
            <div className="lib-page-header__subtitle">Record a book return and apply a late fine if overdue</div>
          </div>
        </div>
        <div className="lib-page-header__actions">
          <button className="rb-btn rb-btn-ghost" onClick={() => navigate(`${sectionBase}/issues`)}>
            <i className="ti ti-arrow-left" /> Back
          </button>
        </div>
      </div>

      <div className="rb-grid">
        {/* Issue summary */}
        <div className="rb-card">
          <div className="rb-card-header">Issue Details</div>
          <div className="rb-card-body">
            <dl className="rb-dl">
              <dt>Book</dt>
              <dd>{bookName}</dd>
              <dt>Member</dt>
              <dd>{memberName}</dd>
              <dt>Copy</dt>
              <dd>{copyText}</dd>
              <dt>Issue Date</dt>
              <dd>{issue.issue_date ? String(issue.issue_date).slice(0, 10) : '-'}</dd>
              <dt>Due Date</dt>
              <dd>{issue.due_date ? String(issue.due_date).slice(0, 10) : '-'}</dd>
              <dt>Status</dt>
              <dd>
                <span
                  className={`rb-badge ${
                    alreadyReturned ? 'rb-badge-success' : overdueDays > 0 ? 'rb-badge-warning' : 'rb-badge-primary'
                  }`}
                >
                  {alreadyReturned ? 'Returned' : overdueDays > 0 ? 'Overdue' : 'Issued'}
                </span>
              </dd>
            </dl>
          </div>
        </div>

        {/* Return form */}
        <div className="rb-card">
          <div className="rb-card-header">Return Form</div>
          <div className="rb-card-body">
            {alreadyReturned ? (
              <div className="rb-alert-success">
                This book has already been returned
                {issue.return_date ? ` on ${String(issue.return_date).slice(0, 10)}` : ''}.
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="rb-form-row">
                  <div className="rb-field">
                    <label className="rb-label">Return Date *</label>
                    <input
                      type="date"
                      className="rb-input"
                      value={returnDate}
                      max={todayStr()}
                      onChange={(e) => setReturnDate(e.target.value)}
                      required
                    />
                  </div>

                  <div className="rb-field">
                    <label className="rb-label">Book Condition</label>
                    <SearchableSelect
                      name="condition"
                      value={condition}
                      onChange={(e) => setCondition(e.target.value)}
                      options={[
                        { value: 'good', label: 'Good' },
                        { value: 'damaged', label: 'Damaged' },
                        { value: 'lost', label: 'Lost' }
                      ]}
                      clearable={false}
                      className="rb-select"
                    />
                  </div>
                </div>

                <div className="rb-field">
                  <label className="rb-label">Remarks</label>
                  <textarea
                    className="rb-textarea"
                    rows="2"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Optional notes about this return"
                  />
                </div>

                {/* Scan verification */}
                <div style={{
                  border: '1px solid #e2e8f0', borderRadius: 10,
                  padding: '14px 16px', marginBottom: 16, background: '#f8fafc'
                }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 12, textTransform: 'uppercase', letterSpacing: .5 }}>
                    <i className="fas fa-barcode me-1" />Scan Verification (optional)
                  </div>

                  {/* Book scan */}
                  <div style={{ marginBottom: 10 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>
                      Verify Book Copy
                    </label>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input
                        type="text"
                        className="rb-input"
                        style={{ flex: 1 }}
                        placeholder="Scan book barcode / accession no"
                        value={bookScan}
                        onChange={(e) => { setBookScan(e.target.value); setBookVerify(null); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleVerifyBook(bookScan); } }}
                        disabled={verifyLoading.book}
                      />
                      <button
                        type="button"
                        className="rb-btn rb-btn-ghost"
                        style={{ padding: '6px 12px', fontSize: 13 }}
                        onClick={() => handleVerifyBook(bookScan)}
                        disabled={verifyLoading.book || !bookScan.trim()}
                      >
                        {verifyLoading.book ? <span className="spinner-border spinner-border-sm" /> : 'Verify'}
                      </button>
                      {bookVerify === 'ok' && <span style={{ color: '#16a34a', fontWeight: 700, fontSize: 18 }}>✓</span>}
                      {bookVerify === 'mismatch' && <span style={{ color: '#dc2626', fontWeight: 700, fontSize: 18, title: 'Copy does not match this issue' }}>✗</span>}
                    </div>
                    {bookVerify === 'mismatch' && (
                      <div style={{ fontSize: 11, color: '#dc2626', marginTop: 3 }}>Scanned copy does not match this issue</div>
                    )}
                  </div>

                  {/* Member scan */}
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>
                      Verify Member
                    </label>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input
                        type="text"
                        className="rb-input"
                        style={{ flex: 1 }}
                        placeholder="Scan member card / QR code"
                        value={memberScan}
                        onChange={(e) => { setMemberScan(e.target.value); setMemberVerify(null); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleVerifyMember(memberScan); } }}
                        disabled={verifyLoading.member}
                      />
                      <button
                        type="button"
                        className="rb-btn rb-btn-ghost"
                        style={{ padding: '6px 12px', fontSize: 13 }}
                        onClick={() => handleVerifyMember(memberScan)}
                        disabled={verifyLoading.member || !memberScan.trim()}
                      >
                        {verifyLoading.member ? <span className="spinner-border spinner-border-sm" /> : 'Verify'}
                      </button>
                      {memberVerify === 'ok' && <span style={{ color: '#16a34a', fontWeight: 700, fontSize: 18 }}>✓</span>}
                      {memberVerify === 'mismatch' && <span style={{ color: '#dc2626', fontWeight: 700, fontSize: 18 }}>✗</span>}
                    </div>
                    {memberVerify === 'mismatch' && (
                      <div style={{ fontSize: 11, color: '#dc2626', marginTop: 3 }}>Scanned card does not match this member</div>
                    )}
                  </div>
                </div>

                {overdueDays > 0 && (
                  <div className="rb-fine-box">
                    <div className="rb-check">
                      <input
                        type="checkbox"
                        id="apply-fine"
                        checked={applyFine}
                        onChange={(e) => setApplyFine(e.target.checked)}
                      />
                      <label htmlFor="apply-fine">
                        Apply late fine ({overdueDays} day{overdueDays > 1 ? 's' : ''} overdue
                        {perDayRate > 0 ? `, suggested ₹ ${suggestedFine.toLocaleString('en-IN')}` : ''})
                      </label>
                    </div>

                    {applyFine && (
                      <div className="rb-fine-fields">
                        <div>
                          <label className="rb-label">Fine Amount (₹)</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="rb-input"
                            value={fineAmount}
                            onChange={(e) => setFineAmount(e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="rb-label">Fine Reason</label>
                          <input
                            type="text"
                            className="rb-input"
                            value={fineReason}
                            onChange={(e) => setFineReason(e.target.value)}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="rb-form-actions">
                  <button
                    type="button"
                    className="rb-btn rb-btn-ghost"
                    onClick={() => navigate(`${sectionBase}/issues`)}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="rb-btn rb-btn-primary" disabled={saving}>
                    {saving ? 'Returning...' : 'Return Book'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReturnBook;
